import { useCallback, useMemo } from 'react';

import { useSearchPageData } from '#src/app/pages/search/pageContext';
import {
	SEARCH_INDEX_NAME_BASE,
	SEARCH_PRESET_BASE,
	SEARCH_VECTOR_COLLECTION_LANGUAGE,
	SEARCH_VECTOR_COLLECTION_VARIATION,
	SEARCH_VECTOR_INDEX_NAME_EN,
	SEARCH_VECTOR_PRESET_EN,
} from '#src/app/pages/search/shared/consts';
import { useLocale } from '#src/app/shared/hooks/l10n';
import { useTypesenseConfig } from '#src/app/shared/hooks/typesense';

type Result = ModelHookResult<{
	searchIndexName: string;
	searchPreset: string;
}>;

export function useSearchIndexName(): Result {
	const locale = useLocale();
	const [language] = locale.split('-');

	const { namespace } = useTypesenseConfig();

	const {
		data: {
			experiments: { searchVectorCollection },
		},
	} = useSearchPageData();

	const useVectorCollectionForLanguage =
		searchVectorCollection.variation === SEARCH_VECTOR_COLLECTION_VARIATION &&
		language === SEARCH_VECTOR_COLLECTION_LANGUAGE;

	const addNamespace = useCallback(
		(indexName: string): string => {
			return namespace ? `${namespace}_${indexName}` : indexName;
		},
		[namespace],
	);

	return {
		data: useMemo(() => {
			return {
				searchIndexName: addNamespace(
					useVectorCollectionForLanguage ? SEARCH_VECTOR_INDEX_NAME_EN : `${SEARCH_INDEX_NAME_BASE}${language}`,
				),
				searchPreset: addNamespace(
					useVectorCollectionForLanguage ? SEARCH_VECTOR_PRESET_EN : `${SEARCH_PRESET_BASE}${language}`,
				),
			};
		}, [addNamespace, language, useVectorCollectionForLanguage]),
	};
}
